import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Divider,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Schedule as ScheduleIcon,
  Message as MessageIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import axios from 'axios';

interface Template {
  id: string;
  name: string;
  description?: string;
  message_text: string;
  channel_type: string;
}

interface CallTemplate {
  id: string;
  name: string;
  description?: string;
  script_content: string;
  assistant_id?: string;
  phone_number_id?: string;
  assistant_config_id?: string;
  status: 'active' | 'inactive';
}

interface VapiAssistant {
  id: string;
  name: string;
  description?: string;
  assistant_id: string;
  phone_number_id: string;
  status: 'active' | 'inactive';
  is_default: boolean;
}

interface CampaignStep {
  id?: string;
  campaign_id: string;
  step_order: number;
  template_id: string;
  delay_days: number;
  delay_hours: number;
  delay_minutes: number;
  conditions?: any;
  step_type?: 'message' | 'call';
  call_template_id?: string;
}

interface CampaignStepsManagerProps {
  campaignId: string;
  onStepsChange: (steps: CampaignStep[]) => void;
}

import { getApiUrl } from '../config/environment';

const API_BASE_URL = getApiUrl();

const CampaignStepsManager: React.FC<CampaignStepsManagerProps> = ({
  campaignId,
  onStepsChange
}) => {
  const [steps, setSteps] = useState<CampaignStep[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [callTemplates, setCallTemplates] = useState<CallTemplate[]>([]);
  const [vapiAssistants, setVapiAssistants] = useState<VapiAssistant[]>([]);
  const [openStepDialog, setOpenStepDialog] = useState(false);
  const [editingStep, setEditingStep] = useState<CampaignStep | null>(null);
  const [stepForm, setStepForm] = useState<CampaignStep>({
    campaign_id: campaignId,
    step_order: 1,
    template_id: '',
    call_template_id: '',
    delay_days: 0,
    delay_hours: 24,
    delay_minutes: 0,
    step_type: 'message'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadTemplates();
    loadCallTemplates();
    loadVapiAssistants();
    loadSteps();
  }, [campaignId]);

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/followups/templates`);
      setTemplates(response.data.data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadCallTemplates = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/vapi/call-templates`);
      setCallTemplates(response.data.data || []);
    } catch (error) {
      console.error('Error loading call templates:', error);
    }
  };

  const loadVapiAssistants = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/vapi/assistants`);
      setVapiAssistants(response.data.data || []);
    } catch (error) {
      console.error('Error loading VAPI assistants:', error);
    }
  };

  const loadSteps = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/followups/campaigns/${campaignId}/steps`);
      const stepsData = response.data.data || [];
      setSteps(stepsData.sort((a: CampaignStep, b: CampaignStep) => a.step_order - b.step_order));
      onStepsChange(stepsData);
    } catch (error) {
      console.error('Error loading steps:', error);
    }
  };

  const handleAddStep = () => {
    setEditingStep(null);
    setStepForm({
      campaign_id: campaignId,
      step_order: steps.length + 1,
      template_id: '',
      call_template_id: '',
      delay_days: 0,
      delay_hours: 24,
      delay_minutes: 0,
      step_type: 'message'
    });
    setOpenStepDialog(true);
  };

  const handleEditStep = (step: CampaignStep) => {
    setEditingStep(step);
    setStepForm({
      ...step,
      campaign_id: campaignId
    });
    setOpenStepDialog(true);
  };

  const handleDeleteStep = async (stepId: string) => {
    try {
      await axios.delete(`${API_BASE_URL}/followups/campaigns/${campaignId}/steps/${stepId}`);
      await loadSteps();
    } catch (error) {
      console.error('Error deleting step:', error);
    }
  };

  const handleSaveStep = async () => {
    try {
      setLoading(true);
      
      let endpoint: string;
      let payload: any;

      if (stepForm.step_type === 'call') {
        // Para steps de ligação, usar endpoint de followups com step_type=call
        endpoint = `${API_BASE_URL}/followups/campaigns/${campaignId}/steps`;
        payload = {
          step_type: 'call',
          call_template_id: stepForm.call_template_id,
          step_order: stepForm.step_order,
          delay_hours: stepForm.delay_hours,
          delay_days: stepForm.delay_days,
          delay_minutes: stepForm.delay_minutes
        };
        
        // ✅ DEBUG: Log do payload sendo enviado
        console.log('🔍 Call step payload:', payload);
        console.log('🔍 Step form state:', stepForm);
      } else {
        // Para steps de mensagem, usar endpoint tradicional
        if (editingStep?.id) {
          endpoint = `${API_BASE_URL}/followups/campaigns/${campaignId}/steps/${editingStep.id}`;
        } else {
          endpoint = `${API_BASE_URL}/followups/campaigns/${campaignId}/steps`;
        }
        payload = stepForm;
      }

      if (editingStep?.id && stepForm.step_type !== 'call') {
        await axios.put(endpoint, payload);
      } else {
        await axios.post(endpoint, payload);
      }
      
      setOpenStepDialog(false);
      setEditingStep(null);
      await loadSteps();
    } catch (error) {
      console.error('Error saving step:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTemplateName = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    return template ? template.name : 'Template não encontrado';
  };

  const getCallTemplateName = (templateId: string) => {
    const template = callTemplates.find(t => t.id === templateId);
    return template ? template.name : 'Template de ligação não encontrado';
  };

  const getTemplatePreview = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    return template ? template.message_text.substring(0, 50) + '...' : '';
  };

  const getCallTemplatePreview = (templateId: string) => {
    const template = callTemplates.find(t => t.id === templateId);
    return template ? template.script_content.substring(0, 50) + '...' : '';
  };

  const getStepDisplayInfo = (step: CampaignStep) => {
    if (step.step_type === 'call' && step.call_template_id) {
      return {
        name: getCallTemplateName(step.call_template_id),
        preview: getCallTemplatePreview(step.call_template_id),
        icon: <PhoneIcon />,
        type: 'Ligação VAPI'
      };
    } else {
      return {
        name: getTemplateName(step.template_id),
        preview: getTemplatePreview(step.template_id),
        icon: <MessageIcon />,
        type: 'Mensagem'
      };
    }
  };

  const formatDelay = (days: number, hours: number, minutes: number = 0) => {
    const parts = [];
    
    if (days > 0) parts.push(`${days} dia(s)`);
    if (hours > 0) parts.push(`${hours} hora(s)`);
    if (minutes > 0) parts.push(`${minutes} minuto(s)`);
    
    if (parts.length === 0) {
      return 'Imediato';
    } else if (parts.length === 1) {
      return parts[0];
    } else if (parts.length === 2) {
      return `${parts[0]} e ${parts[1]}`;
    } else {
      return `${parts[0]}, ${parts[1]} e ${parts[2]}`;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          <MessageIcon sx={{ mr: 1 }} />
          Sequência de Mensagens
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddStep}
        >
          Adicionar Step
        </Button>
      </Box>

      {steps.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Nenhum step configurado. Adicione pelo menos um step para que a campanha funcione.
        </Alert>
      ) : (
        <List>
          {steps.map((step, index) => {
            const stepInfo = getStepDisplayInfo(step);
            return (
              <Card key={step.id || index} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <DragIcon sx={{ mr: 2, color: 'text.secondary' }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                        <Chip
                          label={`Step ${step.step_order}`}
                          color="primary"
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        <Chip
                          label={stepInfo.type}
                          color={step.step_type === 'call' ? 'success' : 'default'}
                          size="small"
                          icon={stepInfo.icon}
                          variant="outlined"
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1">
                          {stepInfo.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {stepInfo.preview}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                          <ScheduleIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            Envio: {formatDelay(step.delay_days, step.delay_hours, step.delay_minutes || 0)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => handleEditStep(step)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => step.id && handleDeleteStep(step.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </List>
      )}

      {/* Step Dialog */}
      <Dialog open={openStepDialog} onClose={() => setOpenStepDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingStep ? 'Editar Step' : 'Novo Step'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Ordem do Step"
              type="number"
              value={stepForm.step_order}
              onChange={(e) => setStepForm({ ...stepForm, step_order: parseInt(e.target.value) })}
              inputProps={{ min: 1 }}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Tipo de Ação</InputLabel>
              <Select
                value={stepForm.step_type || 'message'}
                onChange={(e) => setStepForm({ 
                  ...stepForm, 
                  step_type: e.target.value as 'message' | 'call',
                  template_id: e.target.value === 'call' ? '' : stepForm.template_id,
                  call_template_id: e.target.value === 'message' ? '' : stepForm.call_template_id
                })}
                label="Tipo de Ação"
              >
                <MenuItem value="message">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <MessageIcon sx={{ mr: 1 }} />
                    Enviar Mensagem
                  </Box>
                </MenuItem>
                <MenuItem value="call">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PhoneIcon sx={{ mr: 1 }} />
                    Fazer Ligação (VAPI)
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {stepForm.step_type === 'message' ? (
              <>
                <FormControl fullWidth>
                  <InputLabel>Template de Mensagem</InputLabel>
                  <Select
                    value={stepForm.template_id}
                    onChange={(e) => setStepForm({ ...stepForm, template_id: e.target.value })}
                    label="Template de Mensagem"
                  >
                    {templates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        <Box>
                          <Typography variant="body1">{template.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {template.message_text.substring(0, 50)}...
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {stepForm.template_id && (
                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>Prévia do template:</strong>
                    </Typography>
                    <Typography variant="body2">
                      {templates.find(t => t.id === stepForm.template_id)?.message_text}
                    </Typography>
                  </Alert>
                )}
              </>
            ) : (
              <>
                <FormControl fullWidth>
                  <InputLabel>Template de Ligação</InputLabel>
                  <Select
                    value={stepForm.call_template_id || ''}
                    onChange={(e) => setStepForm({ ...stepForm, call_template_id: e.target.value })}
                    label="Template de Ligação"
                  >
                    {callTemplates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        <Box>
                          <Typography variant="body1">{template.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {template.description || 'Sem descrição'}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {stepForm.call_template_id && (
                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>Script da ligação:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ maxHeight: 100, overflow: 'auto' }}>
                      {callTemplates.find(t => t.id === stepForm.call_template_id)?.script_content}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Assistente:</strong> {
                        (() => {
                          const template = callTemplates.find(t => t.id === stepForm.call_template_id);
                          if (template?.assistant_config_id) {
                            const assistant = vapiAssistants.find(a => a.id === template.assistant_config_id);
                            return assistant ? assistant.name : 'Assistente não encontrado';
                          }
                          const defaultAssistant = vapiAssistants.find(a => a.is_default);
                          return defaultAssistant ? `${defaultAssistant.name} (Padrão)` : 'Assistente padrão';
                        })()
                      }
                    </Typography>
                  </Alert>
                )}

                <Alert severity="warning">
                  <Typography variant="body2">
                    <strong>Importante:</strong> Ligações VAPI só serão realizadas para contatos que possuem número de telefone cadastrado.
                    As ligações respeitam um limite de 1 ligação por minuto.
                  </Typography>
                </Alert>
              </>
            )}

            <Divider />

            <Typography variant="h6">Configuração de Delay</Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  label="Dias"
                  type="number"
                  value={stepForm.delay_days}
                  onChange={(e) => setStepForm({ ...stepForm, delay_days: parseInt(e.target.value) || 0 })}
                  inputProps={{ min: 0 }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Horas"
                  type="number"
                  value={stepForm.delay_hours}
                  onChange={(e) => setStepForm({ ...stepForm, delay_hours: parseInt(e.target.value) || 0 })}
                  inputProps={{ min: 0 }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Minutos"
                  type="number"
                  value={stepForm.delay_minutes}
                  onChange={(e) => setStepForm({ ...stepForm, delay_minutes: parseInt(e.target.value) || 0 })}
                  inputProps={{ min: 0 }}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Alert severity="info">
              <Typography variant="body2">
                Este step será enviado após <strong>{formatDelay(stepForm.delay_days, stepForm.delay_hours, stepForm.delay_minutes)}</strong> do step anterior.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStepDialog(false)}>Cancelar</Button>
          <Button 
            onClick={handleSaveStep} 
            variant="contained"
            disabled={
              loading || 
              (stepForm.step_type === 'message' && !stepForm.template_id) ||
              (stepForm.step_type === 'call' && !stepForm.call_template_id)
            }
          >
            {loading ? 'Salvando...' : (editingStep ? 'Atualizar' : 'Criar')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignStepsManager; 